import { AnimatedSection, MeshBackground } from '@denqfabrik/landing-ui';
import { useMemo } from 'react';
import { useTranslation } from '../../i18n';
import { AuditGraphic, ClassificationGraphic, VerificationGraphic } from './SafetyPipelineGraphics';
import SafetyPrincipleCard from './SafetyPrincipleCard';

const CARD_GRAPHICS = [ClassificationGraphic, VerificationGraphic, AuditGraphic];

export default function ProcessSection() {
  const { lang } = useTranslation();

  const copy = useMemo(
    () =>
      lang === 'de'
        ? {
            headingA: 'Sichere KI-Agenten',
            headingB: 'by design',
            intro:
              'Jede Entscheidung durchläuft eine kontrollierte Pipeline — von der Eingabe bis zum Audit Trail. Kein Agent geht live ohne Sicherheitsarchitektur.',
            cards: [
              {
                number: '01',
                title: 'Input & Klassifikation',
                description:
                  'Anfragen werden normalisiert, auf Risikosignale geprüft und klassifiziert. Fälle außerhalb trainierter Muster werden gestoppt — nicht geraten.',
                bullets: [
                  'Input-Validierung & Schema-Checks',
                  'Unbekannte Muster automatisch erkennen',
                  'Confidence-Schwellen erzwingen Eskalation',
                ],
              },
              {
                number: '02',
                title: 'Entscheidung & Verifikation',
                description:
                  'Kritische Entscheidungen werden unabhängig verifiziert. Bei Uneinigkeit entscheidet ein Mensch — nie das Modell allein.',
                bullets: [
                  'Judge/Jury — mehrstufige Verifikation',
                  'Minority Vote triggert Human Review',
                  'Temporale Checkpoints für Regelversionen',
                ],
              },
              {
                number: '03',
                title: 'Guardrails & Audit',
                description:
                  'Berechtigungen begrenzen Fehlerreichweite. Jeder Schritt wird protokolliert — nicht nur was passierte, sondern warum.',
                bullets: [
                  'Policy Engine & Least Privilege',
                  'Rollback-fähige Aktionspfade',
                  'EU AI Act ready — Art. 13 Compliance',
                ],
              },
            ],
            linkText: 'Sicherheitsarchitektur im Detail',
            linkButton: 'Mehr erfahren',
            linkHref: `/${lang}/sicherheit`,
          }
        : {
            headingA: 'Secure AI agents',
            headingB: 'by design',
            intro:
              'Every decision passes through a controlled pipeline — from input to audit trail. No agent goes live without safety architecture.',
            cards: [
              {
                number: '01',
                title: 'Input & Classification',
                description:
                  'Requests are normalized, checked for risk signals and classified. Cases outside trained patterns are stopped — not guessed.',
                bullets: [
                  'Input validation & schema checks',
                  'Detect unknown patterns automatically',
                  'Confidence thresholds enforce escalation',
                ],
              },
              {
                number: '02',
                title: 'Decision & Verification',
                description:
                  'Critical decisions are independently verified. When models disagree, a human decides — never the model alone.',
                bullets: [
                  'Judge/Jury — multi-layer verification',
                  'Minority vote triggers human review',
                  'Temporal checkpoints for rule versions',
                ],
              },
              {
                number: '03',
                title: 'Guardrails & Audit',
                description:
                  'Permissions limit blast radius. Every step is logged — not just what happened, but why.',
                bullets: [
                  'Policy engine & least privilege',
                  'Rollback-capable action paths',
                  'EU AI Act ready — Art. 13 compliance',
                ],
              },
            ],
            linkText: 'Security architecture in detail',
            linkButton: 'Learn more',
            linkHref: `/${lang}/sicherheit`,
          },
    [lang],
  );

  return (
    <section id="process" className="relative py-20 md:py-28">
      <div className="w-full px-1">
        <div
          data-nav-theme="dark"
          className="relative isolate overflow-hidden rounded-[18px] bg-[#0a1628]"
        >
          <MeshBackground mode="container" />

          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 z-[1]"
            style={{
              background:
                'radial-gradient(1200px 680px at 18% 4%, rgba(60, 110, 210, 0.14), transparent 62%), radial-gradient(900px 520px at 82% 22%, rgba(45, 102, 198, 0.14), transparent 66%), linear-gradient(180deg, rgba(6,11,22,0.18) 0%, rgba(7,12,24,0.32) 50%, rgba(7,13,26,0.48) 100%)',
            }}
          />

          <div className="relative z-10 px-4 py-14 sm:px-6 sm:py-16 lg:px-5 lg:py-16">
            <AnimatedSection>
              {/* Mobile: stacked. XL: two-column zigzag */}
              <div className="flex flex-col gap-8 xl:hidden">
                {/* Mobile heading */}
                <div>
                  <p className="font-display text-[clamp(2.3rem,4.6vw,4.8rem)] font-medium leading-[1.08] tracking-[-0.028em] text-[#cfe0ff]">
                    {copy.headingA}
                    <span className="block bg-[linear-gradient(120deg,#4f8dff_10%,#62c7ff_62%,#61e3ce_100%)] bg-clip-text text-transparent">
                      {copy.headingB}
                    </span>
                  </p>
                  <p className="mt-6 max-w-xl text-[clamp(1.05rem,1.35vw,1.35rem)] leading-[1.4] text-[var(--df-text-mid)]">
                    {copy.intro}
                  </p>
                </div>
                {/* Mobile cards stacked */}
                {copy.cards.map((card, i) => (
                  <SafetyPrincipleCard
                    key={card.number}
                    card={card}
                    graphic={CARD_GRAPHICS[i]}
                    graphicHeightClass="h-[200px]"
                  />
                ))}
                {/* CTA — mobile */}
                <div className="flex flex-col items-center gap-4 pt-4 text-center">
                  <h5 className="text-[1.15rem] font-semibold text-[var(--df-text-mid)]">
                    {copy.linkText}
                  </h5>
                  <a
                    href={copy.linkHref}
                    className="inline-flex items-center gap-2 rounded-2xl bg-sky-100 px-6 py-3.5 text-base font-medium text-slate-900 transition-all duration-300 hover:bg-white"
                  >
                    {copy.linkButton}
                    <svg
                      className="h-4 w-4"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path
                        d="M5 12H19M12 5L19 12L12 19"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </a>
                </div>
              </div>

              {/* Desktop: two-column zigzag layout */}
              <div className="mx-auto hidden w-[80%] xl:grid xl:grid-cols-2 xl:gap-8">
                {/* ── Left column: Heading → Card 02 ── */}
                <div className="flex flex-col gap-10">
                  {/* Heading block — pushed down */}
                  <div className="pb-6 pt-8">
                    <p className="font-display text-[clamp(2.3rem,4.6vw,4.8rem)] font-medium leading-[1.08] tracking-[-0.028em] text-[#cfe0ff]">
                      {copy.headingA}
                      <span className="block bg-[linear-gradient(120deg,#4f8dff_10%,#62c7ff_62%,#61e3ce_100%)] bg-clip-text text-transparent">
                        {copy.headingB}
                      </span>
                    </p>
                    <p className="mt-6 max-w-xl text-[clamp(1.05rem,1.35vw,1.35rem)] leading-[1.4] text-[var(--df-text-mid)]">
                      {copy.intro}
                    </p>
                  </div>

                  {/* Card 02 — Entscheidung & Verifikation */}
                  <SafetyPrincipleCard
                    card={copy.cards[1]}
                    graphic={VerificationGraphic}
                    graphicHeightClass="h-[340px]"
                    className="flex flex-col [animation-delay:100ms]"
                  />

                  {/* CTA — desktop, under Card 02 */}
                  <div className="flex flex-col items-center gap-4 pt-2 text-center">
                    <h5 className="text-[1.25rem] font-semibold text-[var(--df-text-mid)]">
                      {copy.linkText}
                    </h5>
                    <a
                      href={copy.linkHref}
                      className="inline-flex items-center gap-2 rounded-2xl bg-sky-100 px-7 py-4 text-base font-medium text-slate-900 transition-all duration-300 hover:bg-white"
                    >
                      {copy.linkButton}
                      <svg
                        className="h-4 w-4"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <path
                          d="M5 12H19M12 5L19 12L12 19"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </a>
                  </div>
                </div>

                {/* ── Right column: Card 01 → Card 03, pushed down 130px ── */}
                <div className="mt-[160px] flex flex-col gap-10">
                  {/* Card 01 — Input & Klassifikation */}
                  <SafetyPrincipleCard
                    card={copy.cards[0]}
                    graphic={ClassificationGraphic}
                    graphicHeightClass="h-[340px]"
                    className="flex flex-col [animation-delay:0ms]"
                  />

                  {/* Card 03 — Guardrails & Audit */}
                  <SafetyPrincipleCard
                    card={copy.cards[2]}
                    graphic={AuditGraphic}
                    graphicHeightClass="h-[340px]"
                    className="flex flex-col [animation-delay:200ms]"
                  />
                </div>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </div>
    </section>
  );
}
